import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SEO from "../SEO";
import config from "../../config";
import api from "../../utils/api";
import { useTheSeaContext } from "./TheSeaContext";
import { loadGoogleSdk } from "../../utils/loadGoogleSdk";
import { decodeJwtPayload } from "../../utils/jwt";

const inputStyle = {
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid #D4DEE8",
  fontSize: "16px",
  width: "100%",
  maxWidth: "360px",
};

const buttonStyle = {
  padding: "10px 20px",
  borderRadius: "6px",
  border: "none",
  background: "#2B9AAB",
  color: "#fff",
  fontSize: "0.95rem",
  fontWeight: 600,
  cursor: "pointer",
  marginTop: "6px",
};

export default function TheSeaLoginSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/";
  const { login } = useTheSeaContext();

  const [mode, setMode] = useState(location.state?.mode === "login" ? "login" : "signup"); // "signup" | "login"
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Shared by both auth methods: gates the Google button (any mode) and, in signup mode, the
  // native "Create free account" button. Returning users logging in with email/password don't
  // need to re-agree, so it's not required for the native login submit.
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // "form" is the signup/login screen; "verify" is the post-signup (or blocked-login) code entry
  // screen. Native accounts start Pending until the emailed code is confirmed, so no session/JWT
  // exists until this step succeeds.
  const [step, setStep] = useState("form");
  const [pendingEmail, setPendingEmail] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  const [googleSdkLoaded, setGoogleSdkLoaded] = useState(false);
  const consentAgreedRef = useRef(false);
  // Google's own button is only re-rendered when the SDK first loads (see the effect below), not
  // on every signup/login toggle, so its callback closure would otherwise see a stale `mode` —
  // this ref is how it reads the current one at click time instead.
  const modeRef = useRef(mode);

  const isSignup = mode === "signup";
  const nativeSubmitDisabled =
    submitting ||
    (isSignup && !consentAgreed) ||
    (!isSignup && (!email.trim() || !password));

  useEffect(() => {
    consentAgreedRef.current = consentAgreed;
  }, [consentAgreed]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    loadGoogleSdk()
      .then(() => setGoogleSdkLoaded(true))
      .catch((err) => console.error("Google SDK load error:", err));
  }, []);

  // Render Google's own Sign-In button once its SDK is ready. The credential callback handles
  // both new and returning accounts in one call: /anonymous/signup's CreateUser is a no-op for
  // an already-existing federated user, so it doubles as "log in" for them.
  useEffect(() => {
    if (!googleSdkLoaded) return;

    const container = document.getElementById("googleSignInDiv");
    if (!container || !window.google) return;

    const handleCredentialResponse = async (response) => {
      if (!consentAgreedRef.current) {
        setError("Please agree to the Terms of Service and Privacy Policy to continue with Google.");
        return;
      }

      setError("");
      setSubmitting(true);
      try {
        const { data } = await api.post("/anonymous/signup", {
          tokenType: "GoogleToken",
          token: response.credential,
          consentAgreed: true,
        });

        const payload = decodeJwtPayload(response.credential);
        login(payload?.email ? { ...data, email: payload.email } : data);
        navigate(returnTo);
      } catch (err) {
        const backendError = err.response?.data?.error;
        // "an account with this email already exists" only makes sense as signup feedback (it
        // means a native account already owns this email) — on the login page it reads as
        // nonsensical ("I'm trying to log in, of course an account exists"), so show the generic
        // failure there instead.
        if (backendError === "an account with this email already exists" && modeRef.current === "login") {
          setError("Google login failed. Please try again.");
        } else {
          setError(backendError || "Google sign-in failed. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    };

    container.innerHTML = ""; // clear any previously rendered button

    window.google.accounts.id.initialize({
      client_id: config.GISClientId,
      callback: handleCredentialResponse,
    });

    window.google.accounts.id.renderButton(container, {
      theme: "filled_blue",
      size: "large",
      width: 300,
      text: "continue_with",
    });
  }, [googleSdkLoaded, navigate, login, returnTo]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (isSignup) {
      if (!userName.trim()) {
        setError("Please enter your full name.");
        return;
      }
      if (!consentAgreed) {
        setError("Please agree to the Terms of Service and Privacy Policy to continue.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const path = isSignup ? "/anonymous/native_signup" : "/anonymous/native_login";
      const body = isSignup
        ? { userName: userName.trim(), email, password, consentAgreed }
        : { email, password };

      const { data } = await api.post(path, body);

      if (isSignup) {
        // Account is created Pending — no session yet. Move to the code-entry screen; the user
        // is only logged in once native_verify_email succeeds.
        setPendingEmail(email);
        setCode("");
        setInfoMessage("");
        setStep("verify");
        return;
      }

      // The backend never returns a plaintext email (it only stores a one-way hash for
      // dedup), so we attach the address the user just typed for display purposes only;
      // every other field in `data` — userName in particular — is authoritative from the
      // backend's login/signup response.
      login({ ...data, email });
      navigate(returnTo);
    } catch (err) {
      if (!isSignup && err.response?.status === 403) {
        // Account exists but email isn't verified yet — send them to the same code-entry
        // screen (with a resend option) instead of a dead-end error.
        setPendingEmail(email);
        setCode("");
        setInfoMessage("");
        setStep("verify");
        return;
      }
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    setVerifying(true);
    try {
      const { data } = await api.post("/anonymous/native_verify_email", { email: pendingEmail, code: code.trim() });
      login({ ...data, email: pendingEmail });
      navigate(returnTo);
    } catch (err) {
      setError(err.response?.data?.error || "Invalid or expired code. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const resendCode = async () => {
    setError("");
    setInfoMessage("");
    setResending(true);
    try {
      await api.post("/anonymous/native_resend_code", { email: pendingEmail });
      setInfoMessage("A new code has been sent to your email.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to resend code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  // Placed above the Google button on the signup page (check-then-unlock reads naturally when
  // creating a new account) but below it on the login page (returning users shouldn't be made
  // to notice a consent checkbox before they even see the button they came for).
  const consentCheckbox = (
    <label style={{ fontSize: "0.85rem", color: "#50677C", display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "8px" }}>
      <input
        type="checkbox"
        checked={consentAgreed}
        onChange={(e) => setConsentAgreed(e.target.checked)}
        style={{ marginTop: "3px" }}
      />
      <span>
        I agree to the <a href="/terms" style={{ textDecoration: "underline" }}>Terms of Service</a> and{" "}
        <a href="/privacy" style={{ textDecoration: "underline" }}>Privacy Policy</a>.
        {isSignup && " Required to continue with Google or create your account below."}
      </span>
    </label>
  );

  if (step === "verify") {
    return (
      <>
        <SEO
          title="Verify your email — askTheSea"
          description="Enter the verification code we emailed you to activate your askTheSea account."
          url={`${config.BaseUrl}/loginsignup`}
          image={`${config.BaseUrl}/assets/logo.png?v=2`}
          noindex
        />
        <section className="page active" id="page-verify">
          <div className="wrap about-hero">
            <div className="eyebrow">Verify your email</div>
            <h1>Check your inbox</h1>
            <p className="lede">
              We sent a verification code to <strong>{pendingEmail}</strong>. Enter it below to activate your
              account.
            </p>
          </div>

          <div className="wrap">
            <form
              onSubmit={submitVerifyCode}
              style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "360px" }}
            >
              <input
                type="text"
                inputMode="numeric"
                placeholder="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={inputStyle}
                required
                autoFocus
              />
              {error && <p style={{ color: "#c0392b", fontSize: "0.85rem", margin: 0 }}>{error}</p>}
              {infoMessage && <p style={{ color: "#2B9AAB", fontSize: "0.85rem", margin: 0 }}>{infoMessage}</p>}

              <button
                type="submit"
                style={{
                  ...buttonStyle,
                  opacity: verifying || !code.trim() ? 0.5 : 1,
                  cursor: verifying || !code.trim() ? "not-allowed" : "pointer",
                }}
                disabled={verifying || !code.trim()}
              >
                {verifying ? "Verifying…" : "Verify and continue"}
              </button>

              <button
                type="button"
                onClick={resendCode}
                disabled={resending}
                style={{ background: "none", border: "none", color: "#2B9AAB", cursor: resending ? "not-allowed" : "pointer", fontSize: "0.85rem", padding: 0, textAlign: "left" }}
              >
                {resending ? "Resending…" : "Resend code"}
              </button>

              <button
                type="button"
                onClick={() => { setStep("form"); setError(""); setInfoMessage(""); }}
                style={{ background: "none", border: "none", color: "#50677C", cursor: "pointer", fontSize: "0.85rem", padding: 0, textAlign: "left" }}
              >
                Back
              </button>
            </form>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title={isSignup ? "Create your account — askTheSea" : "Log in — askTheSea"}
        description="Create a free account or log in to keep chatting with askTheSea's regulatory intelligence agent."
        url={`${config.BaseUrl}/loginsignup`}
        image={`${config.BaseUrl}/assets/logo.png?v=2`}
        noindex
      />
      <section className="page active" id="page-signup">
        <div className="wrap about-hero">
          <div className="eyebrow">{isSignup ? "Create account" : "Welcome back"}</div>
          <h1>{isSignup ? "Create your free account" : "Log in"}</h1>
          <p className="lede">
            {isSignup
              ? "Keep chatting with askTheSea's agent with a free account."
              : "Log back in to continue your conversation."}
          </p>
        </div>

        <div className="wrap">
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxWidth: "360px", marginBottom: "20px" }}>
            {isSignup && consentCheckbox}
            <div
              id="googleSignInDiv"
              style={{
                opacity: consentAgreed ? 1 : 0.5,
                pointerEvents: consentAgreed ? "auto" : "none",
                transition: "opacity 0.2s",
                minHeight: "40px",
              }}
            />
            {!isSignup && consentCheckbox}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "#50677C",
                fontSize: "0.8rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                margin: "18px 0 6px",
              }}
            >
              <hr style={{ flex: 1, border: "none", borderTop: "1px solid #B7C5D3" }} />
              Or continue with email
              <hr style={{ flex: 1, border: "none", borderTop: "1px solid #B7C5D3" }} />
            </div>
          </div>

          <form
            onSubmit={submit}
            style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "360px" }}
          >
            {isSignup && (
              <input
                type="text"
                placeholder="Full name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                style={inputStyle}
                required
              />
            )}
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              required
            />
            {isSignup && (
              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyle}
                required
              />
            )}
            {error && <p style={{ color: "#c0392b", fontSize: "0.85rem", margin: 0 }}>{error}</p>}

            <button
              type="submit"
              style={{
                ...buttonStyle,
                opacity: nativeSubmitDisabled ? 0.5 : 1,
                cursor: nativeSubmitDisabled ? "not-allowed" : "pointer",
              }}
              disabled={nativeSubmitDisabled}
            >
              {submitting ? "Please wait…" : isSignup ? "Create free account" : "Log in"}
            </button>

            <button
              type="button"
              onClick={() => { setMode(isSignup ? "login" : "signup"); setError(""); }}
              style={{ background: "none", border: "none", color: "#2B9AAB", cursor: "pointer", fontSize: "0.85rem", padding: 0, textAlign: "left" }}
            >
              {isSignup ? "Already have an account? Log in" : "Need an account? Create one"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
